import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import './ChatPreview.css';
import ChatDetail from './ChatDetail';
import Cookies from 'js-cookie';
import PencilIcon from 'shared/icons/PencilIcon';
import TagIcon from 'shared/icons/TagIcon';
import TagModal from 'features/chat/components/TagModal';
import BackButton from 'shared/ui/BackButton';
import { hasSubAccountPermission } from 'utils/permissions';
import { decodeJwt, isAdminPayload } from 'utils/jwt';
import { MODELS_API_BASE_URL } from 'config/apiUrls';

import { useLocation } from 'react-router-dom';

// Helper pour extraire la couleur de fond d'un tag (hex ou linear gradient)
function getTagBackgroundStyle(color) {
  if (!color) return { backgroundColor: '#6366f1' };
  
  if (color.type === 'hex' && typeof color.value === 'string') {
    return { backgroundColor: color.value };
  }
  
  if (color.type === 'linear' && color.value && Array.isArray(color.value.colors)) {
    const { inclination = 135, colors } = color.value;
    const gradientStops = colors
      .map(([position, hexColor]) => `${hexColor} ${position * 100}%`)
      .join(', ');
    return { background: `linear-gradient(${inclination}deg, ${gradientStops})` };
  }
  
  return { backgroundColor: '#6366f1' };
}

// Helper pour extraire la première couleur hex d'un tag (pour le contraste)
function getTagPrimaryColor(color) {
  if (!color) return '#6366f1';
  
  if (color.type === 'hex' && typeof color.value === 'string') {
    return color.value;
  }
  
  if (color.type === 'linear' && color.value && Array.isArray(color.value.colors)) {
    // Prendre la couleur du milieu du dégradé pour un meilleur contraste
    const colors = color.value.colors;
    const midIndex = Math.floor(colors.length / 2);
    return colors[midIndex]?.[1] || '#6366f1';
  }
  
  return '#6366f1';
}

const CURRENCY_SYMBOLS = { EUR: '€', USD: '$', GBP: '£' };

function getExpenseTotal(expenses) {
  if (!Array.isArray(expenses) || expenses.length === 0) return null;
  const primary = expenses.find(e => typeof e?.amount === 'number' && e.amount > 0);
  if (!primary) return null;
  const code = typeof primary.currency === 'string' ? primary.currency.toUpperCase() : '';
  const symbol = CURRENCY_SYMBOLS[code] || '';
  return {
    amount: primary.amount,
    label: `${Math.round(primary.amount)}${symbol}`,
  };
}

// Helper pour le contraste de couleur
function getContrastColor(hexColor) {
  if (!hexColor) return '#ffffff';
  if (typeof hexColor !== 'string') return '#ffffff';
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#1a1a2e' : '#ffffff';
}

const getConversationKey = (conversation, fallbackSubscriptionId = '') =>
  `${conversation?.subscriptionId || fallbackSubscriptionId || 'subscription'}:${conversation?._id || 'conversation'}`;

const getConversationTimestamp = (conversation) => {
  const rawDate = conversation?.lastMessage?.date || conversation?.updatedAt || conversation?.date;
  if (!rawDate) return 0;
  const timestamp = new Date(rawDate).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const sortConversationsByRecentMessage = (items) =>
  [...items].sort((left, right) => getConversationTimestamp(right) - getConversationTimestamp(left));

const CONVERSATION_PAGE_SIZE = 50;
const EMPTY_SUBSCRIPTIONS = [];

const mergeConversations = (current, incoming, replace = false, keepOrder = false) => {
  const byKey = new Map();
  if (!replace) {
    current.forEach((conversation) => {
      byKey.set(getConversationKey(conversation), conversation);
    });
  }
  incoming.forEach((conversation) => {
    byKey.set(getConversationKey(conversation), conversation);
  });
  const mergedConversations = Array.from(byKey.values());
  return keepOrder ? mergedConversations : sortConversationsByRecentMessage(mergedConversations);
};

const getSubscriptionModelName = (subscription) =>
  subscription?.creator?.name || subscription?.creator?.userName || subscription?.creator?.id || 'Modèle';

const isMobileViewport = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(max-width: 768px)').matches;

const ChatPreview = ({
  onBack,
  modeSelection,
  fanList,
  subscriptionId,
  canChat,
  isAgentActive,
  skipTimeWaster = false,
  modelId,
  initialTarget,
  onOpenConversation,
  onCloseConversation,
  subscriptions = EMPTY_SUBSCRIPTIONS,
  isGlobalChat = false,
  splitView = false,
  title = 'Chat',
}) => {
  const { search } = useLocation();
  const [conversations, setConversations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [error, setError] = useState(null);
  const [editingConversationId, setEditingConversationId] = useState(null);
  const [nickname, setNickname] = useState('');
  const [openMenuFor, setOpenMenuFor] = useState(null);
  const [isMobileLayout, setIsMobileLayout] = useState(isMobileViewport);

  // States for filtering and pagination
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [filters, setFilters] = useState({
    notSeenOnly: false,
    onlineOnly: false,
    userName: '',
  });
  const [tagFilter, setTagFilter] = useState('');
  const [tagIdsBySubscriptionId, setTagIdsBySubscriptionId] = useState({});
  const [minSpentFilter, setMinSpentFilter] = useState('');
  const minSpentAmount = useMemo(() => {
    const amount = Number(minSpentFilter);
    return Number.isFinite(amount) && amount > 0 ? amount : 0;
  }, [minSpentFilter]);
  const useMostGenerousSort = minSpentAmount > 0;

  const [showScroll, setShowScroll] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [timewastersByModelId, setTimewastersByModelId] = useState({});
  const suppressAutoOpenRef = useRef(false);
  const searchInputRef = useRef(null);

  // États pour la gestion des tags
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [tagModalConversation, setTagModalConversation] = useState(null);

  // Dépenses par fanId (null = chargé sans dépense, string = total formaté)
  const [expensesByFanId, setExpensesByFanId] = useState({});
  const [expenseAmountsByFanId, setExpenseAmountsByFanId] = useState({});
  const expensesByFanIdRef = useRef({});
  const expenseAmountsByFanIdRef = useRef({});
  const expensesInFlightRef = useRef(new Set());
  const splitLayoutActive = splitView && !isMobileLayout;

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleViewportChange = () => setIsMobileLayout(mediaQuery.matches);

    handleViewportChange();
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleViewportChange);
    } else {
      mediaQuery.addListener?.(handleViewportChange);
    }

    return () => {
      if (typeof mediaQuery.removeEventListener === 'function') {
        mediaQuery.removeEventListener('change', handleViewportChange);
      } else {
        mediaQuery.removeListener?.(handleViewportChange);
      }
    };
  }, []);

  useEffect(() => {
    const checkScrollTop = () => {
      if (!showScroll && window.pageYOffset > 400){
        setShowScroll(true)
      } else if (showScroll && window.pageYOffset <= 400){
        setShowScroll(false)
      }
    };

    window.addEventListener('scroll', checkScrollTop)
    return () => {
      window.removeEventListener('scroll', checkScrollTop)
    }
  }, [showScroll]);

  const scrollTop = () => {
    window.scrollTo({top: 0, behavior: 'smooth'});
  };

  const isJwtAdmin = useMemo(() => isAdminPayload(decodeJwt(Cookies.get('user_token'))), []);

  const activeChatSubscriptions = useMemo(() => {
    if (isGlobalChat) {
      return (Array.isArray(subscriptions) ? subscriptions : [])
        .filter(subscription =>
          subscription?._id &&
          subscription?.is_sub_account === true &&
          hasSubAccountPermission(subscription?.sub_account_permissions, 'can_chat', isJwtAdmin)
        )
        .map(subscription => ({
          subscriptionId: subscription._id,
          canChat: true,
          modelId: subscription?.creator?.id,
          modelName: getSubscriptionModelName(subscription),
          modeSelection: subscription?.mode_selection,
          fanList: Array.isArray(subscription?.fan_list) ? subscription.fan_list : [],
          isAgentActive: subscription?.monitor_status,
          skipTimeWaster: subscription?.skip_time_waster === true,
        }));
    }

    if (!subscriptionId) return [];

    return [{
      subscriptionId,
      canChat,
      modelId,
      modelName: null,
      modeSelection,
      fanList: Array.isArray(fanList) ? fanList : [],
      isAgentActive,
      skipTimeWaster: skipTimeWaster === true,
    }];
  }, [
    canChat,
    fanList,
    isAgentActive,
    isJwtAdmin,
    isGlobalChat,
    modeSelection,
    modelId,
    skipTimeWaster,
    subscriptionId,
    subscriptions,
  ]);

  // Load real tags per subscription so the global chat can group filters by tag name.
  useEffect(() => {
    let isCancelled = false;

    const fetchTags = async () => {
      if (activeChatSubscriptions.length === 0) {
        setTagIdsBySubscriptionId({});
        return;
      }

      const jwt = Cookies.get('user_token');
      if (!jwt) {
        setTagIdsBySubscriptionId({});
        return;
      }

      const entries = await Promise.all(activeChatSubscriptions.map(async (subscription) => {
        try {
          const response = await axios.post(
            'https://unbot.io/api/v1.0/proxy',
            {
              method: 'GET',
              path: 'v2.0/tags/',
              parameters: {},
              subscriptionId: subscription.subscriptionId,
            },
            { headers: { Authorization: `Bearer ${jwt}` } }
          );
          const tags = response.data?.result && Array.isArray(response.data.result)
            ? response.data.result
            : (Array.isArray(response.data) ? response.data : []);
          const tagIdsByName = {};
          tags.forEach((tag) => {
            const name = typeof tag?.name === 'string' ? tag.name.trim() : '';
            const id = typeof tag?._id === 'string' ? tag._id.trim() : '';
            if (name && id && !tagIdsByName[name]) {
              tagIdsByName[name] = id;
            }
          });
          return [subscription.subscriptionId, tagIdsByName];
        } catch (error) {
          console.error('Error fetching tags:', error);
          return [subscription.subscriptionId, {}];
        }
      }));

      if (!isCancelled) {
        setTagIdsBySubscriptionId(Object.fromEntries(entries));
      }
    };

    fetchTags();

    return () => {
      isCancelled = true;
    };
  }, [activeChatSubscriptions, reloadTick]);

  // Load timewasters when feature is enabled
  useEffect(() => {
    let isCancelled = false;

    const fetchTimewasters = async () => {
      const subscriptionsWithTimewasters = activeChatSubscriptions.filter(
        subscription => subscription.skipTimeWaster && subscription.modelId
      );

      if (subscriptionsWithTimewasters.length === 0) {
        setTimewastersByModelId({});
        return;
      }

      const jwt = Cookies.get('user_token');
      if (!jwt) {
        setTimewastersByModelId({});
        return;
      }

      try {
        const uniqueSubscriptionsByModelId = Array.from(
          new Map(subscriptionsWithTimewasters.map(subscription => [subscription.modelId, subscription])).values()
        );

        const entries = await Promise.all(uniqueSubscriptionsByModelId.map(async (subscription) => {
          try {
            const response = await axios.get(`${MODELS_API_BASE_URL}/${encodeURIComponent(subscription.modelId)}/timewasters`, {
              headers: { Authorization: `Bearer ${jwt}` },
            });
            const list = Array.isArray(response.data) ? response.data : [];
            return [subscription.modelId, list];
          } catch (error) {
            console.error('Error fetching timewasters:', error);
            return [subscription.modelId, []];
          }
        }));

        if (isCancelled) return;
        setTimewastersByModelId(Object.fromEntries(entries));
      } catch (error) {
        console.error('Error fetching timewasters:', error);
        if (!isCancelled) {
          setTimewastersByModelId({});
        }
      }
    };

    fetchTimewasters();

    return () => {
      isCancelled = true;
    };
  }, [activeChatSubscriptions, reloadTick]);

  const normalizedFanList = useMemo(
    () => (Array.isArray(fanList) ? fanList : []),
    [fanList]
  );

  const timewasterUsernameSetsByModelId = useMemo(() => {
    return Object.entries(timewastersByModelId).reduce((acc, [currentModelId, list]) => {
      const usernames = Array.isArray(list)
        ? list
          .map(item => item?.fan_username)
          .filter(Boolean)
          .map(name => name.toLowerCase())
        : [];
      acc[currentModelId] = new Set(usernames);
      return acc;
    }, {});
  }, [timewastersByModelId]);

  const getBotResponseStatus = useCallback(
    (conversation) => {
      if (!conversation) return null;

      const mode = conversation.modeSelection || modeSelection || 'all';
      const conversationFanList = Array.isArray(conversation.fanList)
        ? conversation.fanList
        : normalizedFanList;
      const conversationIsAgentActive = conversation.isAgentActive ?? isAgentActive;
      const conversationSkipTimeWaster = conversation.skipTimeWaster ?? skipTimeWaster;
      const userName = (conversation.userName || '').toLowerCase();
      const nickname = (conversation.userNickname || '').toLowerCase();
      const tagName = (conversation.tag?.name || '').toLowerCase();

      if (!conversationIsAgentActive) {
        return {
          willRespond: false,
          tooltip: "L'agent est désactivé",
          isTimewaster: false,
        };
      }

      const timewasterUsernameSet = timewasterUsernameSetsByModelId[conversation.modelId] || new Set();
      if (conversationSkipTimeWaster && timewasterUsernameSet.has(userName)) {
        return {
          willRespond: false,
          tooltip: 'Fan identifié comme timewaster',
          isTimewaster: true,
        };
      }

      // Vérification no_unbot dans le surnom OU le tag
      if (nickname && nickname.includes('no_unbot')) {
        return {
          willRespond: false,
          tooltip: 'Surnom contient "no_unbot"',
          isTimewaster: false,
        };
      }

      if (tagName && tagName.includes('no_unbot')) {
        return {
          willRespond: false,
          tooltip: 'Étiquette contient "no_unbot"',
          isTimewaster: false,
        };
      }

      // Vérification go_unbot dans le surnom OU le tag (mode whitelist)
      if (mode === 'whitelist') {
        if ((nickname && nickname.includes('go_unbot')) || (tagName && tagName.includes('go_unbot'))) {
          return {
            willRespond: true,
            tooltip: nickname.includes('go_unbot') 
              ? 'Surnom contient "go_unbot"' 
              : 'Étiquette contient "go_unbot"',
            isTimewaster: false,
          };
        }
      }

      switch (mode) {
        case 'all':
          return {
            willRespond: true,
            tooltip: "L'agent répondra à ce fan",
            isTimewaster: false,
          };
        case 'whitelist': {
          const isWhitelisted = conversationFanList.includes(userName);
          return {
            willRespond: isWhitelisted,
            tooltip: isWhitelisted
              ? 'Fan présent dans la whitelist'
              : 'Fan absent de la whitelist',
            isTimewaster: false,
          };
        }
        case 'blacklist': {
          const isBlacklisted = conversationFanList.includes(userName);
          return {
            willRespond: !isBlacklisted,
            tooltip: isBlacklisted
              ? 'Fan présent dans la blacklist'
              : 'Fan autorisé',
            isTimewaster: false,
          };
        }
        default:
          return {
            willRespond: false,
            tooltip: 'Configuration inconnue',
            isTimewaster: false,
          };
      }
    },
    [
      isAgentActive,
      modeSelection,
      normalizedFanList,
      skipTimeWaster,
      timewasterUsernameSetsByModelId,
    ]
  );

  const handleConversationClick = useCallback(
    (conversation) => {
      suppressAutoOpenRef.current = false;
      const botStatus = getBotResponseStatus(conversation);
      const conversationWithExtras = {
        ...conversation,
        subscriptionId: conversation.subscriptionId || subscriptionId,
        botWillRespond: botStatus?.willRespond || false,
        botResponseMeta: botStatus || null,
        canChat: conversation.canChat ?? canChat,
      };
      setSelectedConversation(conversationWithExtras);
      if (onOpenConversation) {
        try {
          onOpenConversation(conversationWithExtras, search);
        } catch (_) {}
      }
    },
    [getBotResponseStatus, subscriptionId, canChat, onOpenConversation, search]
  );

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(f => (f.userName === searchTerm ? f : { ...f, userName: searchTerm }));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset conversations and page when filters change
  useEffect(() => {
    setConversations([]);
    setPage(0);
    setHasMore(true);
  }, [filters, tagFilter, useMostGenerousSort]);

  const enrichConversationsForSubscription = useCallback((items, subscription) => {
    return (Array.isArray(items) ? items : []).map(conversation => ({
      ...conversation,
      subscriptionId: subscription.subscriptionId,
      canChat: subscription.canChat,
      modelId: subscription.modelId,
      modelName: subscription.modelName,
      modeSelection: subscription.modeSelection,
      fanList: subscription.fanList,
      isAgentActive: subscription.isAgentActive,
      skipTimeWaster: subscription.skipTimeWaster,
    }));
  }, []);

  const fetchConversationsForSubscription = useCallback(async (subscription, jwt, start, end) => {
    try {
      const tagId = tagFilter
        ? tagIdsBySubscriptionId[subscription.subscriptionId]?.[tagFilter]
        : null;
      if (tagFilter && !tagId) {
        return { error: false, hasMore: false, conversations: [] };
      }

      const parameters = {
        start,
        end,
        notSeenOnly: filters.notSeenOnly,
        userName: filters.userName,
        onlineOnly: filters.onlineOnly,
        deleted: false,
        sort: 'DESC'
      };
      if (tagId) {
        parameters.tagId = tagId;
      }

      const response = await axios.post(
        'https://unbot.io/api/v1.0/proxy',
        {
          method: 'GET',
          path: useMostGenerousSort ? 'v2.0/conversations/mostGenerous' : 'v2.0/conversations/me',
          parameters,
          subscriptionId: subscription.subscriptionId
        },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );

      if (response.data.error || response.error) {
        return { error: true, hasMore: false, conversations: [] };
      }

      const newConversations = response.data.conversations || [];
      return {
        error: false,
        hasMore: newConversations.length === CONVERSATION_PAGE_SIZE,
        conversations: enrichConversationsForSubscription(newConversations, subscription),
      };
    } catch (error) {
      console.error('Error fetching conversations:', error);
      return { error: true, hasMore: false, conversations: [] };
    }
  }, [enrichConversationsForSubscription, filters, tagFilter, tagIdsBySubscriptionId, useMostGenerousSort]);

  // Fetching logic
  useEffect(() => {
    const fetchConversations = async () => {
      if (activeChatSubscriptions.length === 0) {
        setConversations([]);
        setHasMore(false);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        const jwt = Cookies.get('user_token');
        const start = page * CONVERSATION_PAGE_SIZE;
        const end = (page + 1) * CONVERSATION_PAGE_SIZE;
        const results = await Promise.all(
          activeChatSubscriptions.map(subscription =>
            fetchConversationsForSubscription(subscription, jwt, start, end)
          )
        );

        const successfulResults = results.filter(result => !result.error);

        if (successfulResults.length === 0) {
          setError("Impossible de charger les conversations. Etes-vous sur d'avoir les permissions nécessaires ?");
          setConversations([]);
          setHasMore(false);
          return;
        }

        const newConversations = successfulResults.flatMap(result => result.conversations);
        setConversations(prev => mergeConversations(prev, newConversations, page === 0, useMostGenerousSort));
        setHasMore(successfulResults.some(result => result.hasMore));

      } catch (error) {
        console.error('Error fetching conversations:', error);
        setError('Une erreur est survenue lors du chargement des conversations.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchConversations();
  }, [
    activeChatSubscriptions,
    fetchConversationsForSubscription,
    page,
    reloadTick,
    useMostGenerousSort,
  ]);

  // Lazy batch-fetch des dépenses totales par fan
  useEffect(() => {
    if (!conversations.length) return;
    const jwt = Cookies.get('user_token');
    if (!jwt) return;

    const pendingExpenseRequests = conversations
      .map(conv => ({
        fanId: conv.fanId,
        subscriptionId: conv.subscriptionId || subscriptionId,
      }))
      .filter(item =>
        typeof item.fanId === 'string' &&
        item.fanId.length > 0 &&
        typeof item.subscriptionId === 'string' &&
        item.subscriptionId.length > 0
      )
      .filter(item => {
        const key = `${item.subscriptionId}:${item.fanId}`;
        return !(key in expensesByFanIdRef.current) && !expensesInFlightRef.current.has(key);
      });

    if (pendingExpenseRequests.length === 0) return;

    const CONCURRENCY = 4;

    const fetchOne = async ({ fanId, subscriptionId: expenseSubscriptionId }) => {
      const expenseKey = `${expenseSubscriptionId}:${fanId}`;
      expensesInFlightRef.current.add(expenseKey);
      try {
        const response = await axios.post(
          'https://unbot.io/api/v1.0/proxy',
          {
            method: 'GET',
            path: 'v2.0/users/expenses/chat',
            parameters: { fanId },
            subscriptionId: expenseSubscriptionId,
          },
          { headers: { Authorization: `Bearer ${jwt}` } }
        );
        const total = getExpenseTotal(response.data?.expenses);
        expensesByFanIdRef.current[expenseKey] = total?.label || null;
        expenseAmountsByFanIdRef.current[expenseKey] = total?.amount ?? null;
        setExpensesByFanId(prev => ({ ...prev, [expenseKey]: total?.label || null }));
        setExpenseAmountsByFanId(prev => ({ ...prev, [expenseKey]: total?.amount ?? null }));
      } catch (err) {
        expensesByFanIdRef.current[expenseKey] = null;
        expenseAmountsByFanIdRef.current[expenseKey] = null;
        setExpensesByFanId(prev => ({ ...prev, [expenseKey]: null }));
        setExpenseAmountsByFanId(prev => ({ ...prev, [expenseKey]: null }));
      } finally {
        expensesInFlightRef.current.delete(expenseKey);
      }
    };

    (async () => {
      for (let i = 0; i < pendingExpenseRequests.length; i += CONCURRENCY) {
        const batch = pendingExpenseRequests.slice(i, i + CONCURRENCY);
        await Promise.all(batch.map(fetchOne));
      }
    })();
  }, [conversations, subscriptionId]);

  // Open initial target if provided (by username or by id)
  useEffect(() => {
    if (!initialTarget || selectedConversation) return;
    if (suppressAutoOpenRef.current) return;
    if (!conversations || conversations.length === 0) return;
    const target = initialTarget.toLowerCase();
    const match = conversations.find(c => (c.userName && c.userName.toLowerCase() === target) || (c._id === initialTarget));
    if (match) {
      handleConversationClick(match);
    }
  }, [initialTarget, conversations, selectedConversation, handleConversationClick]);

  // Close conversation when route target is cleared
  useEffect(() => {
    if (initialTarget === '' && selectedConversation) {
      setSelectedConversation(null);
    }
    if (initialTarget === '') {
      suppressAutoOpenRef.current = false;
    }
  }, [initialTarget, selectedConversation]);

  // Auto-refresh for real-time updates
  useEffect(() => {
    const refreshConversations = async () => {
      if (activeChatSubscriptions.length === 0) return;

      try {
        const jwt = Cookies.get('user_token');
        const results = await Promise.all(
          activeChatSubscriptions.map(subscription =>
            fetchConversationsForSubscription(subscription, jwt, 0, CONVERSATION_PAGE_SIZE)
          )
        );
        const newConversations = results
          .filter(result => !result.error)
          .flatMap(result => result.conversations);
        setConversations(prev => mergeConversations(prev, newConversations, false, useMostGenerousSort));
      } catch (error) {
        console.error('Error refreshing conversations:', error);
      }
    };

    const interval = setInterval(refreshConversations, 10000);
    return () => clearInterval(interval);
  }, [activeChatSubscriptions, fetchConversationsForSubscription, useMostGenerousSort]);

  // Close the options menu when clicking outside or pressing Escape
  useEffect(() => {
    const handleGlobalClick = (e) => {
      if (!openMenuFor) return;
      const t = e.target;
      // If click is inside the menu or the button, keep it open
      if (t && t.closest && (t.closest('.options-menu') || t.closest('.options-button'))) {
        return;
      }
      setOpenMenuFor(null);
    };
    const handleKey = (e) => {
      if (openMenuFor && e.key === 'Escape') setOpenMenuFor(null);
    };
    document.addEventListener('click', handleGlobalClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('click', handleGlobalClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [openMenuFor]);

  // IntersectionObserver for infinite scroll
  const observer = useRef();
  const lastConversationElementRef = useCallback(node => {
    if (isLoading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [isLoading, hasMore]);

  const handleNicknameUpdate = async (conversation, originalNickname) => {
    const trimmedNickname = nickname.trim();
    const conversationId = conversation?._id;
    const currentSubscriptionId = conversation?.subscriptionId || subscriptionId;
    const conversationKey = getConversationKey(conversation, subscriptionId);

    if (trimmedNickname === (originalNickname || '')) {
      setEditingConversationId(null);
      setNickname('');
      return;
    }

    if (!conversationId || !currentSubscriptionId) return;

    try {
      const jwt = Cookies.get('user_token');
      await axios.post(
        'https://unbot.io/api/v1.0/proxy',
        {
          method: 'PUT',
          path: 'v2.0/conversations/nickname',
          parameters: {
            conversationId: conversationId,
            fanNickname: trimmedNickname,
          },
          subscriptionId: currentSubscriptionId,
        },
        {
          headers: {
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      setConversations(prev =>
        prev.map(conv =>
          getConversationKey(conv, subscriptionId) === conversationKey
            ? { ...conv, userNickname: trimmedNickname }
            : conv
        )
      );
      setSelectedConversation(prev =>
        prev && getConversationKey(prev, subscriptionId) === conversationKey
          ? { ...prev, userNickname: trimmedNickname }
          : prev
      );
      setEditingConversationId(null);
      setNickname('');
    } catch (error) {
      console.error('Error updating nickname:', error);
      alert('Erreur lors de la mise à jour du pseudonyme.');
      setEditingConversationId(null);
      setNickname('');
    }
  };


  const handleFilterChange = (e) => {
    const { name, checked } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const expandSearch = () => {
    if (!splitView) return;
    setIsSearchExpanded(true);
    setTimeout(() => searchInputRef.current?.focus(), 0);
  };

  const collapseSearch = () => {
    setSearchTerm('');
    setIsSearchExpanded(false);
    searchInputRef.current?.blur();
  };

  const handleSearchBlur = () => {
    if (splitView && !searchTerm.trim()) {
      setIsSearchExpanded(false);
    }
  };

  const handleBackFromDetail = () => {
    suppressAutoOpenRef.current = true;
    setSelectedConversation(null);
    window.scrollTo(0, 0);
    if (onCloseConversation) {
      try {
        onCloseConversation();
      } catch (_) {}
    }
    return true;
  };

  const handleManualReload = () => {
    setError(null);
    setConversations([]);
    setPage(0);
    setHasMore(true);
    setExpensesByFanId({});
    setExpenseAmountsByFanId({});
    expensesByFanIdRef.current = {};
    expenseAmountsByFanIdRef.current = {};
    expensesInFlightRef.current.clear();
    setReloadTick(previous => previous + 1);
  };

  const toggleSeenStatus = async (conversation, currentStatus) => {
    const conversationId = conversation?._id;
    const currentSubscriptionId = conversation?.subscriptionId || subscriptionId;
    const conversationKey = getConversationKey(conversation, subscriptionId);
    if (!conversationId || !currentSubscriptionId) return;

    try {
      const jwt = Cookies.get('user_token');
      const newStatus = !currentStatus;
      await axios.post(
        'https://unbot.io/api/v1.0/proxy',
        {
          method: 'POST',
          path: 'v2.0/conversations/seen',
          parameters: {
            conversationId: conversationId,
            status: newStatus
          },
          subscriptionId: currentSubscriptionId
        },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );
      setConversations(prev =>
        prev.map(conv =>
          getConversationKey(conv, subscriptionId) === conversationKey
            ? { ...conv, seen: newStatus }
            : conv
        )
      );
      setSelectedConversation(prev =>
        prev && getConversationKey(prev, subscriptionId) === conversationKey
          ? { ...prev, seen: newStatus }
          : prev
      );
    } catch (error) {
      console.error('Error updating conversation status:', error);
    }
  };

  const handleBlockFan = async (conversation, originalNickname) => {
    const conversationId = conversation?._id;
    const currentSubscriptionId = conversation?.subscriptionId || subscriptionId;
    const conversationKey = getConversationKey(conversation, subscriptionId);
    if (!conversationId || !currentSubscriptionId) return;

    try {
      if (!window.confirm('Bloquer ce fan ? Il sera masqué de la liste. L\'agent ne pourra plus lui répondre.')) return;
      // Optimistic update: hide immediately
      const blockedNickname = 'no_unbot_blocked';
      setConversations(prev =>
        prev.map(conv =>
          getConversationKey(conv, subscriptionId) === conversationKey
            ? { ...conv, userNickname: blockedNickname }
            : conv
        )
      );
      setOpenMenuFor(null);

      const jwt = Cookies.get('user_token');
      await axios.post(
        'https://unbot.io/api/v1.0/proxy',
        {
          method: 'PUT',
          path: 'v2.0/conversations/nickname',
          parameters: {
            conversationId: conversationId,
            fanNickname: blockedNickname,
          },
          subscriptionId: currentSubscriptionId,
        },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );
    } catch (err) {
      console.error('Error blocking fan:', err);
      // Rollback on failure
      setConversations(prev =>
        prev.map(conv =>
          getConversationKey(conv, subscriptionId) === conversationKey
            ? { ...conv, userNickname: originalNickname || '' }
            : conv
        )
      );
      alert('Impossible de bloquer ce fan pour le moment.');
    }
  };

  const handleOpenTagModal = (e, conversation) => {
    e.stopPropagation();
    
    // Si le fan a déjà un tag, demander confirmation de suppression
    if (conversation.tag) {
      handleRemoveTag(conversation);
    } else {
      // Sinon, ouvrir le modal pour ajouter un tag
      setTagModalConversation(conversation);
      setTagModalOpen(true);
    }
  };

  const handleRemoveTag = async (conversation) => {
    if (!window.confirm(`Retirer l'étiquette "${conversation.tag.name}" de ce fan ?`)) {
      return;
    }

    try {
      const jwt = Cookies.get('user_token');
      // Extraire l'ID du fan en retirant le préfixe "users/"
      const fanUserId = conversation.userId?.replace('users/', '') || conversation.fanId?.replace('users/', '');

      await axios.post(
        'https://unbot.io/api/v1.0/proxy',
        {
          method: 'DELETE',
          path: `v2.0/tags/user/${fanUserId}`,
          parameters: {},
          subscriptionId: conversation.subscriptionId || subscriptionId,
        },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );

      // Mise à jour optimiste
      const conversationKey = getConversationKey(conversation, subscriptionId);
      setConversations(prev =>
        prev.map(conv =>
          getConversationKey(conv, subscriptionId) === conversationKey
            ? { ...conv, tag: null }
            : conv
        )
      );
      setSelectedConversation(prev =>
        prev && getConversationKey(prev, subscriptionId) === conversationKey
          ? { ...prev, tag: null }
          : prev
      );
    } catch (err) {
      console.error('Error removing tag:', err);
      alert("Erreur lors de la suppression de l'étiquette");
    }
  };

  const handleTagUpdate = (newTag) => {
    if (!tagModalConversation) return;
    const conversationKey = getConversationKey(tagModalConversation, subscriptionId);
    setConversations(prev =>
      prev.map(conv =>
        getConversationKey(conv, subscriptionId) === conversationKey
          ? { ...conv, tag: newTag }
          : conv
      )
    );
    setSelectedConversation(prev =>
      prev && getConversationKey(prev, subscriptionId) === conversationKey
        ? { ...prev, tag: newTag }
        : prev
    );
  };

  useEffect(() => {
    setSelectedConversation(prev => {
      if (!prev) return prev;
      const updatedStatus = getBotResponseStatus(prev);
      if (!updatedStatus) return prev;
      if (
        prev.botWillRespond === updatedStatus.willRespond &&
        prev.botResponseMeta?.tooltip === updatedStatus.tooltip &&
        prev.botResponseMeta?.isTimewaster === updatedStatus.isTimewaster
      ) {
        return prev;
      }
      return {
        ...prev,
        botWillRespond: updatedStatus.willRespond,
        botResponseMeta: updatedStatus,
      };
    });
  }, [getBotResponseStatus]);

  const tagFilterOptions = useMemo(() => {
    const names = new Set();
    Object.values(tagIdsBySubscriptionId).forEach((tagsByName) => {
      Object.keys(tagsByName || {}).forEach((name) => names.add(name));
    });
    return Array.from(names)
      .map((name) => ({ value: name, label: name }))
      .sort((left, right) => left.label.localeCompare(right.label, 'fr', { sensitivity: 'base' }));
  }, [tagIdsBySubscriptionId]);

  const visibleConversations = useMemo(() => (
    conversations.filter((conversation) => {
      if (conversation.userNickname === 'no_unbot_blocked') return false;

      if (minSpentAmount > 0) {
        const expenseKey = `${conversation.subscriptionId || subscriptionId}:${conversation.fanId}`;
        const amount = expenseAmountsByFanId[expenseKey];
        if (typeof amount === 'number' && amount < minSpentAmount) return false;
      }

      return true;
    })
  ), [conversations, expenseAmountsByFanId, minSpentAmount, subscriptionId]);

  const handleMinSpentChange = (event) => {
    const nextValue = event.target.value.replace(',', '.');
    if (/^\d*\.?\d*$/.test(nextValue)) {
      setMinSpentFilter(nextValue);
    }
  };

  const renderConversationList = () => {
    const filterClassName = [
      'chat-filters',
      splitView ? 'compact-filters' : '',
      isSearchExpanded ? 'search-expanded' : 'search-collapsed',
    ].filter(Boolean).join(' ');

    return (
      <>
        <div className={filterClassName}>
          <div
            className="search-bar"
            onClick={expandSearch}
            aria-expanded={splitView ? isSearchExpanded : undefined}
          >
            <i className="fas fa-search"></i>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Rechercher par nom..."
              value={searchTerm}
              onFocus={expandSearch}
              onBlur={handleSearchBlur}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                if (splitView) setIsSearchExpanded(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  collapseSearch();
                }
              }}
            />
            {splitView && isSearchExpanded && (
              <button
                type="button"
                className="search-collapse-button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.stopPropagation();
                  collapseSearch();
                }}
                aria-label="Fermer la recherche"
                title="Fermer la recherche"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
          {(!splitView || !isSearchExpanded) && (
            <div className="filter-options">
              <label className="filter-label">
                <input
                  type="checkbox"
                  name="notSeenOnly"
                  checked={filters.notSeenOnly}
                  onChange={handleFilterChange}
                />
                <i className="fas fa-eye-slash"></i>
                <span>Non lus</span>
              </label>
              <label className="filter-label">
                <input
                  type="checkbox"
                  name="onlineOnly"
                  checked={filters.onlineOnly}
                  onChange={handleFilterChange}
                />
                <i className="fas fa-wifi"></i>
                <span>En ligne</span>
              </label>
              {(tagFilterOptions.length > 0 || tagFilter) && (
                <label className="filter-label filter-control-label">
                  <i className="fas fa-tag"></i>
                  <select
                    className="tag-filter-select"
                    value={tagFilter}
                    onChange={(e) => setTagFilter(e.target.value)}
                    aria-label="Filtrer par étiquette"
                  >
                    <option value="">Étiquette</option>
                    {tagFilter && !tagFilterOptions.some(option => option.value === tagFilter) && (
                      <option value={tagFilter}>Étiquette sélectionnée</option>
                    )}
                    {tagFilterOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label className="filter-label filter-control-label">
                <i className="fas fa-euro-sign"></i>
                <input
                  className="spent-filter-input"
                  type="text"
                  inputMode="decimal"
                  value={minSpentFilter}
                  onChange={handleMinSpentChange}
                  placeholder="Min"
                  aria-label="Montant minimum dépensé"
                />
              </label>
            </div>
          )}
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <ul className="conversation-list">
          {visibleConversations.map((conv, index, arr) => {
            const conversationKey = getConversationKey(conv, subscriptionId);
            const expenseKey = `${conv.subscriptionId || subscriptionId}:${conv.fanId}`;
            const isLastElement = arr.length === index + 1;
            const isSelected = selectedConversation &&
              getConversationKey(selectedConversation, subscriptionId) === conversationKey;
            const botStatus = getBotResponseStatus(conv);
            const indicatorClass = botStatus?.willRespond ? 'will-respond' : 'will-not-respond';
            const indicatorTooltip = botStatus?.tooltip || '';

            return (
              <li
                key={conversationKey}
                className={`${!conv.seen ? 'unread' : ''} ${isSelected ? 'selected' : ''}`.trim()}
                onClick={() => handleConversationClick(conv)}
                ref={isLastElement ? lastConversationElementRef : null}
              >
                <div
                  className={`chat-preview-profile-pic ${conv.online ? 'online' : ''}`}
                  style={{ backgroundImage: `url(${conv.picture?.small || ''})` }}
                  role="img"
                  aria-label={conv.userName}
                />
                <div className="conversation-details">
                  <div className="user-info">
                    <div className="username-line">
                      {botStatus && (
                        <div
                          className={`bot-indicator-dot ${indicatorClass}`}
                          data-tooltip={indicatorTooltip}
                        ></div>
                      )}
                      <strong>{conv.userName}</strong>
                      {isGlobalChat && conv.modelName && (
                        <span className="model-badge-inline">
                          {conv.modelName}
                        </span>
                      )}
                      {editingConversationId !== conversationKey && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingConversationId(conversationKey);
                            setNickname(conv.userNickname || '');
                          }}
                          className="edit-nickname-button"
                        >
                          <PencilIcon className="edit-nickname-icon" />
                        </button>
                      )}
                      <div className="username-tag-inline" onClick={(e) => e.stopPropagation()}>
                        {conv.tag ? (
                          <span
                            className="tag-badge-inline"
                            style={{
                              ...getTagBackgroundStyle(conv.tag.color),
                              color: getContrastColor(getTagPrimaryColor(conv.tag.color)),
                            }}
                            onClick={(e) => handleOpenTagModal(e, conv)}
                            title="Cliquer pour retirer l'étiquette"
                          >
                            {conv.tag.name}
                          </span>
                        ) : (
                          <button
                            className="tag-add-btn-inline"
                            onClick={(e) => handleOpenTagModal(e, conv)}
                            title="Ajouter une étiquette"
                          >
                            <TagIcon className="tag-add-icon" />
                          </button>
                        )}
                      </div>
                      {expensesByFanId[expenseKey] && (
                        <span
                          className="fan-expense-bubble"
                          title={`Total dépensé par ce fan : ${expensesByFanId[expenseKey]}`}
                          aria-label={`Dépenses totales : ${expensesByFanId[expenseKey]}`}
                        >
                          {expensesByFanId[expenseKey]}
                        </span>
                      )}
                    </div>
                    {editingConversationId === conversationKey ? (
                      <input
                        type="text"
                        value={nickname}
                        onChange={(e) => setNickname(e.target.value)}
                        onBlur={() => handleNicknameUpdate(conv, conv.userNickname)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleNicknameUpdate(conv, conv.userNickname);
                          if (e.key === 'Escape') {
                            setEditingConversationId(null);
                            setNickname('');
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                        className="nickname-input"
                        placeholder="Surnom..."
                      />
                    ) : (
                      conv.userNickname && (
                        <div className="user-nickname">Surnom: {conv.userNickname}</div>
                      )
                    )}
                    {botStatus?.isTimewaster && (
                      <div className="timewaster-flag" aria-label="Ce fan est identifié comme timewaster">
                        <i className="fas fa-ban" aria-hidden="true"></i>
                        <span>Timewaster • l'agent ne répondra pas</span>
                      </div>
                    )}
                  </div>
                  <p>{conv.lastMessage?.message || ''}</p>
                  <span className="conversation-date">
                    {conv.lastMessage?.date ? new Date(conv.lastMessage.date).toLocaleString() : ''}
                  </span>
                </div>
                <div className="item-actions" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="options-button"
                    title="Actions"
                    onClick={() => setOpenMenuFor(prev => prev === conversationKey ? null : conversationKey)}
                  >
                    <i className="fas fa-ellipsis-h"></i>
                  </button>
                  {openMenuFor === conversationKey && (
                    <div className="options-menu">
                      <button
                        className="options-item"
                        onClick={() => {
                          toggleSeenStatus(conv, conv.seen);
                          setOpenMenuFor(null);
                        }}
                      >
                        <i className={`fas ${conv.seen ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                        <span>{conv.seen ? 'Marquer comme non lu' : 'Marquer comme lu'}</span>
                      </button>
                      <button className="options-item danger" onClick={() => handleBlockFan(conv, conv.userNickname)}>
                        Bloquer ce fan
                      </button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>

        {isLoading && <p className="chat-preview-loading-message">Chargement...</p>}
        {!hasMore && conversations.length > 0 && <p className="chat-preview-end-message">Vous avez atteint la fin des conversations.</p>}
        {!isLoading && conversations.length > 0 && visibleConversations.length === 0 && !error && (
          <div className="no-conversations">
            <p>Aucune conversation trouvée pour les filtres sélectionnés.</p>
          </div>
        )}
        {!isLoading && conversations.length === 0 && !error && (
          <div className="no-conversations">
            <p>Aucune conversation trouvée pour les filtres sélectionnés.</p>
          </div>
        )}
        <button onClick={scrollTop} className={`scroll-to-top ${showScroll ? 'visible' : ''}`}>
          ↑ Remonter en haut
        </button>
      </>
    );
  };

  if (selectedConversation && !splitLayoutActive) {
    return (
      <ChatDetail
        conversation={selectedConversation}
        onBack={handleBackFromDetail}
      />
    );
  }

  const containerClassName = [
    'chat-preview-container',
    isGlobalChat ? 'global-chat-preview' : '',
    splitView ? 'split-view' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClassName}>
      <BackButton onClick={onBack} />
      {!splitView && (
        <div className="chat-preview-title-row">
          <h2 className="chat-preview-title">{title}</h2>
          <button
            onClick={handleManualReload}
            className="reload-button-common"
            disabled={isLoading}
            aria-label="Recharger les conversations"
            title="Recharger les conversations"
          >
            <i className={`fas fa-sync-alt ${isLoading ? 'fa-spin' : ''}`}></i>
          </button>
        </div>
      )}

      {splitLayoutActive ? (
        <div className="chat-split-layout">
          <aside className="chat-list-panel">
            {renderConversationList()}
          </aside>
          <section className="chat-detail-panel">
            {selectedConversation ? (
              <ChatDetail
                conversation={selectedConversation}
                onBack={handleBackFromDetail}
                hideBackButton
                hideAgentStatus
              />
            ) : (
              <div className="chat-detail-placeholder">
                Sélectionnez une conversation
              </div>
            )}
          </section>
        </div>
      ) : renderConversationList()}

      <TagModal
        isOpen={tagModalOpen}
        onClose={() => {
          setTagModalOpen(false);
          setTagModalConversation(null);
        }}
        subscriptionId={tagModalConversation?.subscriptionId || subscriptionId}
        fanId={tagModalConversation?.fanId}
        currentTag={tagModalConversation?.tag}
        onTagUpdate={handleTagUpdate}
      />
    </div>
  );
};

export default ChatPreview; 
